import { useEffect, useState } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Cross, ProductKeyTypes } from "@/libraries/wpl/foundations/src";

import Link from "../../link/src";

export type ToastComponentProps = {
    product: ProductKeyTypes;
    title: string;
    description: string;
    cta: {
        url: string;
        text: string;
    };
};

export const ToastComponent = (props: ToastComponentProps) => {
    const { product, title, description, cta } = props;
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const [closeToast, setCloseToast] = useState(false);

    const mainContainerStyle = css({
        "opacity": "0",
        "box-shadow": "0 4px 6px 3px #42526e29",
        "width": "280px",
        "background-color": "#ffffff",
        "border-radius": "4px",
        "position": "fixed",
        "z-index": "1000",
        "bottom": "16px",
        "transform": `translate3d(-200px, 0, 0)`,
        "@media (max-width: 600px)": {
            width: "100%",
            transform: `translate3d(0, 200px, 0)`
        },
        "&.closeToastStyle": {
            "opacity": "0",
            "transform": `translate3d(-300px, 0, 0)`,
            "@media (max-width: 600px)": {
                width: "100%",
                transform: `translate3d(0, 300px, 0)`
            },
            "transition": "opacity .8s ease-in-out, transform .6s ease-in-out"
        }
    });

    const contentSectionStyle = css({
        "border-radius": "4px",
        "display": "flex",
        "flex-direction": "column",
        "background-color": "#ffffff",
        "text-align": "left",
        "padding": "24px"
    });
    const imageSectionStyle = css({
        "display": "flex",
        "flex-direction": "row",
        "justify-content": "space-between"
    });
    const showToastAnimationStyle = css({
        "opacity": "1",
        "transform": "translate3d(16px, 0, 0)",
        "transition": "opacity .4s ease-in-out, transform .4s ease-in-out",
        "@media (max-width: 600px)": {
            transform: `translate3d(0, 16px, 0)`
        }
    });

    const titleStyle = css({
        "font-weight": "400",
        "line-height": "1.16",
        "font-family": "var(--bxp-charlie-display-font-family)",
        "font-size": " 1.5rem",
        "margin-top": "16px",
        "margin-bottom": "8px"
    });
    const descriptionStyle = css({
        "margin-top": "8px",
        "margin-bottom": "8px"
    });

    const closePanelIconContainerStyle = css({
        "background": "transparent",
        "border": 0,
        "cursor": "pointer",
        "display": "flex",
        "padding": 0,
        "align-items": "center",
        "insetInlineEnd": token("space.250"),
        "insetBlockStart": token("space.200")
    });

    useEffect(() => {
        setTimeout(() => {
            setShouldAnimate(true);
        }, 100);
    }, []);

    const closeToastCard = () => {
        setCloseToast(true);
    };

    return (
        <div
            css={[mainContainerStyle, shouldAnimate && showToastAnimationStyle]}
            className={closeToast ? `closeToastStyle` : ""}
        >
            <div css={contentSectionStyle}>
                <div css={imageSectionStyle}>
                    <img
                        src="https://wac-cdn.atlassian.com/misc-assets/webp-images/Stars.webp-347416437.webp"
                        alt="toast-stars"
                        width={"66px"}
                    />

                    <button
                        id="toast-close-button"
                        css={closePanelIconContainerStyle}
                        onClick={(event) => {
                            event.preventDefault();
                            return closeToastCard();
                        }}
                        data-testid="toast-close"
                        data-label="toast-close-button"
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container="toast-container"
                        data-product-key={{ product }}
                    >
                        <Cross size="20" />
                    </button>
                </div>
                <h3 css={titleStyle}>{title}</h3>
                <p css={descriptionStyle}>{description}</p>
                <Link
                    href={cta.url}
                    text={cta.text}
                    dataEventProps={{
                        "data-event": "clicked",
                        "data-event-component": "link",
                        "data-event-container": "ctaSection",
                        "data-product-key": product,
                        "data-label": `toast-cta-${cta.text}`
                    }}
                />
            </div>
        </div>
    );
};
